import { ArrayHelpers, FieldArray, FormikProps } from 'formik';
import {
    BillingModality,
    BonusCondition,
    ConditionMode,
    getBillingModalityTranslationId,
    getUnitTranslationId,
} from '@synergy/models';
import { CommentNoteIcon } from '@synergy/theme';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import { PoolTypography as LegacyPoolTypography } from '@synergy/components';
import { PaloColumn, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolButton, SelectOption, ValueOrNDash } from '@synergy/components';
import { PoolFormikSelect, PoolTooltip, PoolTypography } from '@synergy/components-next';
import { RandomFileTypeIcon } from '@synergy/theme-next';
import { getCurrencySymbol } from '@synergy/utils';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import * as SupplierDetailConditionsActions from '../store/supplier-conditions-actions';
import * as SupplierEmployeeActions from '../../employees/store/supplier-employee-actions';
import { SupplierConditionsFormValues } from '../models/supplier-conditions-form-values';
import { SupplierDetailConditionsBonusConditionModal } from './supplier-detail-conditions-bonus-condition-modal';
import { TaxRateAutocomplete } from '../../../../../../common/components/tax-rate-autocomplete/tax-rate-autocomplete';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { supplierEmployeeSelector } from '../../employees/store/supplier-employee-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

type ConditionArrayHelper = ArrayHelpers & {
    form: FormikProps<SupplierConditionsFormValues>;
    name: string;
};

export const SupplierDetailConditionsBonusConditions = () => {
    const dispatch = useThunkDispatch();
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const employees = useAppSelector(supplierEmployeeSelector);
    const locale = useAppSelector(bestLocaleSelector);
    const [bonusCondition, setBonusCondition] = useState<Partial<BonusCondition> | null>(null);

    useEffect(() => {
        if (!employees && supplierIdParam) {
            dispatch(SupplierEmployeeActions.fetchSupplierEmployees(+supplierIdParam));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const billingModalityOptions: SelectOption[] = Object.values(BillingModality).map(value => ({
        value,
        displayValue: <FormattedMessage id={getBillingModalityTranslationId(value)} />,
    }));

    const columns: PaloColumn<BonusCondition>[] = [
        {
            key: 'title',
            type: 'name',
            head: <FormattedMessage id="app.table.column.title" />,
            cell: condition => <ValueOrNDash value={condition.title} />,
        },
        {
            key: 'bonus',
            type: 'percent',
            head: <FormattedMessage id="app.table.column.bonus" />,
            cell: condition => (
                <ValueOrNDash>
                    {(condition.price || condition.percentage) &&
                        (ConditionMode.Amount === condition.mode ? (
                            <>
                                <span>{condition.price}</span>{' '}
                                <span>{condition.currency && getCurrencySymbol(condition.currency, locale)}</span>
                                {' / '}
                                <span>{condition.quantity}</span>{' '}
                                <FormattedMessage id={getUnitTranslationId(condition.unit)} />
                            </>
                        ) : (
                            <span>
                                {condition.percentage} &#37;{' '}
                                <FormattedMessage id="app.contactsAndOrganizations.detail.percentageAddition" />
                            </span>
                        ))}
                </ValueOrNDash>
            ),
        },
        {
            key: 'conditionFrom',
            type: 'date',
            head: <FormattedMessage id="app.table.column.conditionFrom" />,
            cell: condition => (
                <ValueOrNDash>
                    {condition.conditionFrom && (
                        <FormattedDate value={condition.conditionFrom} year="numeric" month="short" day="2-digit" />
                    )}
                </ValueOrNDash>
            ),
        },
        {
            key: 'conditionTo',
            type: 'date',
            head: <FormattedMessage id="app.table.column.conditionTo" />,
            cell: condition => (
                <ValueOrNDash>
                    {condition.conditionTo && (
                        <FormattedDate value={condition.conditionTo} year="numeric" month="short" day="2-digit" />
                    )}
                </ValueOrNDash>
            ),
        },
        {
            key: 'description',
            type: 'icon',
            head: <FormattedMessage id="app.table.column.description" />,
            cell: condition => (
                <PoolTooltip
                    title={condition.description}
                    defaultTitle={<FormattedMessage id="app.contactsAndOrganizations.detail.noComment" />}
                    placement="top"
                >
                    <RandomFileTypeIcon
                        sx={0 === condition.description?.length ? { color: theme => theme.palette.grey[400] } : {}}
                    />
                </PoolTooltip>
            ),
        },
        {
            key: 'internalNote',
            type: 'icon',
            head: <FormattedMessage id="app.label.note" />,
            cell: condition => (
                <PoolTooltip
                    title={condition.internalNote}
                    defaultTitle={<FormattedMessage id="app.contactsAndOrganizations.detail.noNote" />}
                    placement="top"
                >
                    <CommentNoteIcon
                        sx={0 === condition.internalNote?.length ? { color: theme => theme.palette.grey[400] } : {}}
                    />
                </PoolTooltip>
            ),
        },
    ];

    const conditionActions = (helper: ConditionArrayHelper, condition: BonusCondition): PaloRowAction[] => {
        const index = helper.form.values.conditions.indexOf(condition);

        return [
            {
                label: <FormattedMessage id={condition.isArchived ? 'app.common.unarchive' : 'app.common.archive'} />,
                onClick: () => handleArchiveConditionClick(condition, helper, index),
            },
            {
                label: <FormattedMessage id="app.common.edit" />,
                onClick: () => handleBonusConditionEditClick(condition),
            },
            { label: <FormattedMessage id="app.common.delete" />, onClick: () => helper.remove(index) },
        ];
    };

    const handleArchiveConditionClick = (condition: BonusCondition, helper: ConditionArrayHelper, index: number) => {
        helper.replace(index, {
            ...condition,
            isArchived: !condition.isArchived,
        });

        dispatch(SupplierDetailConditionsActions.updateArchiveCondition(condition.id));
    };

    const handleBonusConditionEditClick = (values: BonusCondition) => {
        setBonusCondition({ ...values });
    };

    const handleNewBonusConditionClick = () => {
        setBonusCondition({});
    };

    const handleConditionSubmit = (helper: ConditionArrayHelper) => (values: BonusCondition) => {
        const index = helper.form.values.conditions.findIndex(condition => condition.id === values.id);
        if (index >= 0) {
            helper.replace(index, values);
        } else {
            helper.unshift(values);
        }
        setBonusCondition(null);
    };

    return (
        <FieldArray name="conditions">
            {(helper: ConditionArrayHelper) => (
                <div>
                    <Grid item={true} xs={12}>
                        <LegacyPoolTypography
                            styleType="sectionTitle"
                            id="app.contactsAndOrganizations.detail.bonusCondition"
                        />
                    </Grid>
                    <Grid container={true} spacing={2} sx={{ mb: 4 }}>
                        <Grid container={true} item={true} spacing={1.5}>
                            <Grid item={true} xs={12}>
                                <PoolTypography
                                    typography="body1"
                                    translationId="app.contactsAndOrganizations.detail.bonusCondition.general"
                                    sx={{ mb: 1.5, fontWeight: 600 }}
                                />
                            </Grid>
                            <PaloNextThemeProvider>
                                <Grid item={true} xs={4}>
                                    <PoolFormikSelect
                                        name="billingModality"
                                        options={billingModalityOptions}
                                        label={
                                            <FormattedMessage id="app.contactsAndOrganizations.detail.billingModality" />
                                        }
                                        fullWidth={true}
                                        required={true}
                                    />
                                </Grid>
                                <Grid item={true} xs={4}>
                                    <TaxRateAutocomplete
                                        name="bonusTaxRateId"
                                        useId={true}
                                        label={
                                            <FormattedMessage id="app.contactsAndOrganizations.detail.bonusTaxRate" />
                                        }
                                    />
                                </Grid>
                            </PaloNextThemeProvider>
                        </Grid>
                    </Grid>
                    <Grid item={true} xs={12}>
                        <PoolTypography
                            typography="body1"
                            translationId="app.contactsAndOrganizations.detail.bonusCondition"
                            sx={{ mb: 1.5, fontWeight: 600 }}
                        />
                        {helper.form.values.conditions.length === 0 && (
                            <PoolTypography
                                typography="body1"
                                translationId="app.contactsAndOrganizations.detail.bonusCondition.hint"
                                sx={{ mb: 1.5 }}
                            />
                        )}
                        {helper.form.values.conditions.length > 0 && (
                            <PaloListView<BonusCondition, number>
                                embedded={true}
                                columns={columns}
                                items={helper.form.values.conditions}
                                // A condition being entered has no id yet.
                                getRowId={condition =>
                                    condition.id ?? -1 - helper.form.values.conditions.indexOf(condition)
                                }
                                emptyLabel={
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.bonusCondition.hint" />
                                }
                                // An archived condition stays on the list, only out of play.
                                isRowMuted={condition => !!condition.isArchived}
                                rowActions={condition => conditionActions(helper, condition)}
                            />
                        )}
                        <PoolButton
                            styleType="thirdLevel"
                            startIcon={<AddIcon color="primary" />}
                            onClick={handleNewBonusConditionClick}
                            sx={helper.form.values.conditions.length > 0 ? { mt: 1.5 } : {}}
                        >
                            <FormattedMessage id="app.contactsAndOrganizations.detail.addBonusCondition" />
                        </PoolButton>
                    </Grid>

                    <SupplierDetailConditionsBonusConditionModal
                        bonusCondition={bonusCondition}
                        onBonusConditionSubmit={handleConditionSubmit(helper)}
                        onClose={() => setBonusCondition(null)}
                    />
                </div>
            )}
        </FieldArray>
    );
};
